import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  BarChart3,
  Users,
  MousePointerClick,
  Clock,
  TrendingUp,
  TrendingDown,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  RefreshCw,
  Download,
  Eye,
  Activity,
  Target,
  Zap,
  ArrowRight,
  Calendar,
  Filter,
  Loader2,
  Link2,
  ExternalLink,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Mini chart component for sparklines
const MiniChart = ({ data, color = "#22c55e", height = 40 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data, 1);
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = height - (val / max) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox={`0 0 100 ${height}`} className="w-full" preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
};

// Progress bar for funnel
const FunnelBar = ({ label, count, percentage, color }) => (
  <div className="space-y-2">
    <div className="flex justify-between text-sm">
      <span>{label}</span>
      <span className="font-mono">{count.toLocaleString()}</span>
    </div>
    <div className="h-8 bg-muted rounded-lg overflow-hidden relative">
      <div 
        className="h-full rounded-lg transition-all duration-500"
        style={{ width: `${percentage}%`, backgroundColor: color }}
      />
      <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
        {percentage.toFixed(1)}%
      </span>
    </div>
  </div>
);

const AnalyticsDashboard = ({ getAuthHeaders }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState("30d");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Analytics data
  const [overview, setOverview] = useState(null);
  const [trafficSources, setTrafficSources] = useState(null);
  const [devices, setDevices] = useState(null);
  const [topPages, setTopPages] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [trends, setTrends] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [referrals, setReferrals] = useState(null);
  
  // Calculate date range
  const getDateParams = useCallback(() => {
    if (startDate && endDate) {
      return { start_date: startDate, end_date: endDate };
    }
    
    const end = new Date();
    let start = new Date();
    
    switch (dateRange) {
      case "7d":
        start.setDate(end.getDate() - 7);
        break;
      case "30d":
        start.setDate(end.getDate() - 30);
        break;
      case "90d":
        start.setDate(end.getDate() - 90);
        break;
      case "365d":
        start.setDate(end.getDate() - 365);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }
    
    return {
      start_date: start.toISOString(),
      end_date: end.toISOString()
    };
  }, [dateRange, startDate, endDate]);
  
  // Fetch all analytics data
  const fetchAnalytics = useCallback(async () => {
    setRefreshing(true);
    const params = getDateParams();
    const headers = getAuthHeaders();
    
    try {
      const [
        overviewRes,
        trafficRes,
        devicesRes,
        pagesRes,
        perfRes,
        realtimeRes,
        trendsRes,
        conversionsRes,
        clicksRes,
        referralsRes
      ] = await Promise.all([
        axios.get(`${API_URL}/admin/analytics/overview`, { headers, params }),
        axios.get(`${API_URL}/admin/analytics/traffic-sources`, { headers, params }),
        axios.get(`${API_URL}/admin/analytics/devices`, { headers, params }),
        axios.get(`${API_URL}/admin/analytics/pages`, { headers, params }),
        axios.get(`${API_URL}/admin/analytics/performance`, { headers, params }),
        axios.get(`${API_URL}/admin/analytics/realtime`, { headers }),
        axios.get(`${API_URL}/admin/analytics/trends`, { headers, params: { ...params, granularity: "day" } }),
        axios.get(`${API_URL}/admin/analytics/conversions`, { headers, params }),
        axios.get(`${API_URL}/admin/analytics/clicks`, { headers, params }),
        axios.get(`${API_URL}/admin/analytics/referrals`, { headers, params })
      ]);
      
      setOverview(overviewRes.data);
      setTrafficSources(trafficRes.data);
      setDevices(devicesRes.data);
      setTopPages(pagesRes.data);
      setPerformance(perfRes.data);
      setRealtime(realtimeRes.data);
      setTrends(trendsRes.data);
      setConversions(conversionsRes.data);
      setClicks(clicksRes.data);
      setReferrals(referralsRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, getDateParams]);
  
  useEffect(() => {
    fetchAnalytics();
    
    // Auto-refresh realtime data every 30 seconds
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/analytics/realtime`, { headers: getAuthHeaders() });
        setRealtime(res.data);
      } catch (e) {}
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchAnalytics, getAuthHeaders]);
  
  // Export analytics
  const handleExport = async (format) => {
    try {
      const params = { ...getDateParams(), format };
      const response = await axios.get(`${API_URL}/admin/analytics/export`, {
        headers: getAuthHeaders(),
        params,
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analytics_export.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Analytics exported successfully");
      }
    } catch (error) {
      toast.error("Failed to export analytics");
    }
  };
  
  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };
  
  const getTrafficSourceColor = (source) => {
    const colors = {
      'Direct': '#3b82f6',
      'Organic': '#22c55e',
      'Paid': '#f59e0b',
      'Social': '#ec4899',
      'Referral': '#8b5cf6',
      'Email': '#06b6d4',
      'Campaign': '#f97316'
    };
    return colors[source] || '#6b7280';
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            Website Analytics
          </h2>
          <p className="text-muted-foreground">Track user behavior, performance, and conversions</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="365d">Last year</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={fetchAnalytics} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>
      
      {/* Real-time Banner */}
      {realtime && (
        <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                  <span className="font-medium">Real-time</span>
                </div>
                <div className="text-2xl font-bold">{realtime.active_users}</div>
                <span className="text-muted-foreground">active users right now</span>
              </div>
              <div className="flex items-center gap-2">
                <MiniChart data={realtime.pageviews_timeline?.map(p => p.count) || []} height={30} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Page Views</p>
                <p className="text-3xl font-bold">{overview?.pageviews?.toLocaleString() || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-500 opacity-80" />
            </div>
            {overview?.pageviews_change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${overview.pageviews_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {overview.pageviews_change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {Math.abs(overview.pageviews_change)}% vs previous period
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sessions</p>
                <p className="text-3xl font-bold">{overview?.sessions?.toLocaleString() || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-green-500 opacity-80" />
            </div>
            {overview?.sessions_change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${overview.sessions_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {overview.sessions_change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {Math.abs(overview.sessions_change)}% vs previous period
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unique Visitors</p>
                <p className="text-3xl font-bold">{overview?.unique_visitors?.toLocaleString() || 0}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500 opacity-80" />
            </div>
            {overview?.unique_visitors_change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${overview.unique_visitors_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {overview.unique_visitors_change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {Math.abs(overview.unique_visitors_change)}% vs previous period
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Conversions</p>
                <p className="text-3xl font-bold">{overview?.conversions?.toLocaleString() || 0}</p>
              </div>
              <Target className="w-8 h-8 text-orange-500 opacity-80" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
              <span>{overview?.avg_pages_per_session || 0} pages/session</span>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs for detailed analytics */}
      <Tabs defaultValue="traffic" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="traffic">Traffic</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          <TabsTrigger value="behavior">Behavior</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="conversions">Conversions</TabsTrigger>
          <TabsTrigger value="pages">Pages</TabsTrigger>
        </TabsList>
        
        {/* Traffic Tab */}
        <TabsContent value="traffic" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Traffic Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Traffic Sources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trafficSources?.sources?.map((source, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: getTrafficSourceColor(source.source) }}
                          />
                          {source.source}
                        </span>
                        <span className="font-mono">{source.sessions.toLocaleString()} ({source.percentage}%)</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${source.percentage}%`,
                            backgroundColor: getTrafficSourceColor(source.source)
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  {(!trafficSources?.sources || trafficSources.sources.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">No traffic data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Devices */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="w-5 h-5" />
                  Devices & Browsers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium mb-3">Devices</h4>
                    <div className="space-y-2">
                      {devices?.devices?.slice(0, 5).map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            {getDeviceIcon(d.name)}
                            {d.name}
                          </span>
                          <span className="font-mono">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-3">Browsers</h4>
                    <div className="space-y-2">
                      {devices?.browsers?.slice(0, 5).map((b, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>{b.name}</span>
                          <span className="font-mono">{b.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Trends Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Traffic Trends</CardTitle>
              <CardDescription>Daily pageviews and sessions over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-end gap-1">
                {trends?.trends?.map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${day.date}: ${day.pageviews} views`}>
                    <div 
                      className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                      style={{ 
                        height: `${Math.max(4, (day.pageviews / Math.max(...trends.trends.map(t => t.pageviews), 1)) * 200)}px`
                      }}
                    />
                    {i % 7 === 0 && (
                      <span className="text-xs text-muted-foreground rotate-45 origin-left">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                ))}
                {(!trends?.trends || trends.trends.length === 0) && (
                  <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    No trend data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Referrals Tab */}
        <TabsContent value="referrals" className="space-y-4">
          {/* Referral Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6 text-center">
                <Globe className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="text-sm text-muted-foreground">Total Referral Visits</p>
                <p className="text-3xl font-bold">{referrals?.total_referral_visits?.toLocaleString() || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <ExternalLink className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm text-muted-foreground">Referring Domains</p>
                <p className="text-3xl font-bold">{referrals?.domains?.length || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Link2 className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                <p className="text-sm text-muted-foreground">Unique Referrer URLs</p>
                <p className="text-3xl font-bold">{referrals?.referrals?.length || 0}</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Referring Domains */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Top Referring Domains
                </CardTitle>
                <CardDescription>Websites sending traffic to you</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {referrals?.domains?.slice(0, 10).map((domain, i) => (
                    <div key={i} className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium flex items-center gap-2">
                          <img 
                            src={`https://www.google.com/s2/favicons?domain=${domain.domain}&sz=16`} 
                            alt="" 
                            className="w-4 h-4"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          {domain.domain}
                        </span>
                        <Badge variant="secondary">{domain.total_visits} visits</Badge>
                      </div>
                      {domain.urls?.length > 1 && (
                        <p className="text-xs text-muted-foreground">
                          {domain.urls.length} different pages
                        </p>
                      )}
                    </div>
                  ))}
                  {(!referrals?.domains || referrals.domains.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">No referral data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Detailed Referrer URLs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Referrer URLs
                </CardTitle>
                <CardDescription>Exact pages where visitors came from</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {referrals?.referrals?.slice(0, 20).map((ref, i) => (
                    <div key={i} className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge 
                              variant="outline" 
                              className="shrink-0"
                              style={{ 
                                borderColor: getTrafficSourceColor(ref.traffic_source),
                                color: getTrafficSourceColor(ref.traffic_source)
                              }}
                            >
                              {ref.traffic_source}
                            </Badge>
                            <span className="text-sm font-medium truncate">{ref.domain}</span>
                          </div>
                          <a 
                            href={ref.referrer_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-primary truncate block"
                            title={ref.referrer_url}
                          >
                            {ref.referrer_url}
                          </a>
                          {ref.last_visit && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Last visit: {new Date(ref.last_visit).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-mono text-sm font-bold">
                            {(ref.sessions || ref.pageviews || 0).toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {ref.sessions ? 'sessions' : 'views'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!referrals?.referrals || referrals.referrals.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">No referrer URLs tracked yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Behavior Tab */}
        <TabsContent value="behavior" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Click Tracking */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MousePointerClick className="w-5 h-5" />
                  Top Clicked Elements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {clicks?.clicks?.slice(0, 10).map((click, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Badge variant="outline" className="shrink-0">{click.element_tag}</Badge>
                        <span className="truncate text-sm">
                          {click.element_text || click.element_id || 'Unknown element'}
                        </span>
                      </div>
                      <span className="font-mono text-sm ml-2">{click.clicks} clicks</span>
                    </div>
                  ))}
                  {(!clicks?.clicks || clicks.clicks.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">No click data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {realtime?.recent_pageviews?.map((pv, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                      {getDeviceIcon(pv.device)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{pv.page_url.replace(/^https?:\/\/[^/]+/, '')}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(pv.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {(!realtime?.recent_pageviews || realtime.recent_pageviews.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">No recent activity</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6 text-center">
                <Zap className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                <p className="text-sm text-muted-foreground">Avg Load Time</p>
                <p className="text-3xl font-bold">{performance?.avg_load_time || 0}ms</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="text-sm text-muted-foreground">Time to First Byte</p>
                <p className="text-3xl font-bold">{performance?.avg_ttfb || 0}ms</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <Activity className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm text-muted-foreground">Web Vitals Score</p>
                <p className="text-3xl font-bold">{performance?.web_vitals_score || 'N/A'}</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Core Web Vitals */}
          <Card>
            <CardHeader>
              <CardTitle>Core Web Vitals</CardTitle>
              <CardDescription>Key performance metrics that affect user experience</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>LCP (Largest Contentful Paint)</span>
                    <Badge variant={performance?.avg_lcp < 2500 ? 'default' : performance?.avg_lcp < 4000 ? 'secondary' : 'destructive'}>
                      {performance?.avg_lcp || 0}ms
                    </Badge>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500"
                      style={{ width: `${Math.min(100, (performance?.avg_lcp || 0) / 40)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Good: &lt;2.5s | Needs work: 2.5-4s | Poor: &gt;4s</p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>FID (First Input Delay)</span>
                    <Badge variant={performance?.avg_fid < 100 ? 'default' : performance?.avg_fid < 300 ? 'secondary' : 'destructive'}>
                      {performance?.avg_fid || 0}ms
                    </Badge>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500"
                      style={{ width: `${Math.min(100, (performance?.avg_fid || 0) / 3)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Good: &lt;100ms | Needs work: 100-300ms | Poor: &gt;300ms</p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>CLS (Cumulative Layout Shift)</span>
                    <Badge variant={performance?.avg_cls < 0.1 ? 'default' : performance?.avg_cls < 0.25 ? 'secondary' : 'destructive'}>
                      {performance?.avg_cls?.toFixed(3) || 0}
                    </Badge>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500"
                      style={{ width: `${Math.min(100, (performance?.avg_cls || 0) * 400)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Good: &lt;0.1 | Needs work: 0.1-0.25 | Poor: &gt;0.25</p>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mt-4">
                Based on {performance?.sample_count || 0} samples
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Conversions Tab */}
        <TabsContent value="conversions" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Conversion Funnel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Conversion Funnel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {conversions?.funnel?.map((step, i) => {
                    const maxCount = Math.max(...(conversions?.funnel?.map(s => s.count) || [1]));
                    const percentage = (step.count / maxCount) * 100;
                    return (
                      <div key={i}>
                        <FunnelBar 
                          label={step.step} 
                          count={step.count} 
                          percentage={percentage}
                          color={`hsl(${180 - i * 60}, 70%, 50%)`}
                        />
                        {i < (conversions?.funnel?.length || 0) - 1 && (
                          <div className="flex justify-center my-2">
                            <ArrowRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(!conversions?.funnel || conversions.funnel.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">No funnel data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Custom Events */}
            <Card>
              <CardHeader>
                <CardTitle>Custom Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {conversions?.events?.map((event, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                      <span>{event.name}</span>
                      <Badge variant="secondary">{event.count}</Badge>
                    </div>
                  ))}
                  {(!conversions?.events || conversions.events.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">No custom events tracked yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Pages Tab */}
        <TabsContent value="pages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Pages</CardTitle>
              <CardDescription>Most visited pages on your website</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-4 text-sm font-medium text-muted-foreground border-b pb-2">
                  <div className="col-span-6">Page</div>
                  <div className="col-span-3 text-right">Views</div>
                  <div className="col-span-3 text-right">Unique Visitors</div>
                </div>
                {topPages?.pages?.map((page, i) => (
                  <div key={i} className="grid grid-cols-12 gap-4 py-2 hover:bg-muted/50 rounded-lg transition-colors">
                    <div className="col-span-6 flex items-center gap-2">
                      <span className="text-muted-foreground w-5">{i + 1}.</span>
                      <span className="truncate">{page.page}</span>
                    </div>
                    <div className="col-span-3 text-right font-mono">{page.views.toLocaleString()}</div>
                    <div className="col-span-3 text-right font-mono">{page.unique_visitors.toLocaleString()}</div>
                  </div>
                ))}
                {(!topPages?.pages || topPages.pages.length === 0) && (
                  <p className="text-muted-foreground text-center py-8">No page data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
